<template>
  <div
    v-if="showPrompt"
    class="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 pwa-install-prompt"
  >
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-4">
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <img
            src="/icons/icon-192.png"
            alt="{{ appName }}"
            class="w-12 h-12 rounded-lg"
          />
        </div>
        <div class="ml-3 flex-1">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
            {{  __('Install :appName App', {
              appName: appName
            }) }}
          </h3>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {{ __('For faster access, install the app on your device.') }}
          </p>
          <div class="mt-3 flex space-x-2 space-x-reverse">
            <button
              @click="installApp"
              class="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded-md transition-colors"
            >
              {{ __('Install') }}
            </button>
            <button
              @click="dismissPrompt"
              class="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium py-2 px-4 rounded-md transition-colors"
            >
              {{ __('Later') }}
            </button>
          </div>
        </div>
        <button
          @click="dismissPrompt"
          class="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PWAInstallPrompt',
  data() {
    return {
      showPrompt: false,
      deferredPrompt: null,
      showPromptTimeout: null,
    }
  },

  computed: {
    appName() {
      return Nova.config('appName')
    }
  },

  mounted() {
    // Check if already installed
    if (this.isInstalled()) {
      return
    }

    // Check if user has dismissed the prompt before
    if (this.hasDismissedPrompt()) {
      return
    }

    // Listen for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt)

    // Show prompt after a delay (e.g., 5 seconds after page load) if event hasn't fired yet
    this.showPromptTimeout = setTimeout(() => {
      if (this.deferredPrompt && !this.isInstalled() && !this.showPrompt) {
        this.showPrompt = true
      }
    }, 5000)
  },
  beforeUnmount() {
    window.removeEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt)
    if (this.showPromptTimeout) {
      clearTimeout(this.showPromptTimeout)
    }
  },
  methods: {
    handleBeforeInstallPrompt(e) {
      // Prevent the mini-infobar from appearing
      e.preventDefault()
      // Stash the event so it can be triggered later
      this.deferredPrompt = e
      // Clear the timeout if it exists
      if (this.showPromptTimeout) {
        clearTimeout(this.showPromptTimeout)
        this.showPromptTimeout = null
      }
      // Show the prompt after a short delay for better UX
      setTimeout(() => {
        if (!this.isInstalled() && !this.hasDismissedPrompt()) {
          this.showPrompt = true
        }
      }, 2000)
    },
    async installApp() {
      if (!this.deferredPrompt) {
        return
      }

      // Show the install prompt
      this.deferredPrompt.prompt()

      // Wait for the user to respond to the prompt
      const { outcome } = await this.deferredPrompt.userChoice

      if (outcome === 'accepted') {
        console.log('User accepted the install prompt')
        this.saveInstallStatus()
      } else {
        console.log('User dismissed the install prompt')
      }

      // Clear the deferredPrompt
      this.deferredPrompt = null
      this.showPrompt = false
    },
    dismissPrompt() {
      this.showPrompt = false
      this.saveDismissStatus()
    },
    isInstalled() {
      // Check if running as standalone (installed)
      return (
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true ||
        document.referrer.includes('android-app://')
      )
    },
    hasDismissedPrompt() {
      return localStorage.getItem('pwa-install-dismissed') === 'true'
    },
    saveDismissStatus() {
      localStorage.setItem('pwa-install-dismissed', 'true')
      // Reset after 7 days
      setTimeout(() => {
        localStorage.removeItem('pwa-install-dismissed')
      }, 7 * 24 * 60 * 60 * 1000)
    },
    saveInstallStatus() {
      localStorage.setItem('pwa-installed', 'true')
    },
  },
}
</script>

<style scoped>
.pwa-install-prompt {
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>

